import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const STATUS_BADGE = {
  PENDING_REVIEW: { bg: '#fef9c3', color: '#854d0e' },
  APPROVED:       { bg: '#dcfce7', color: '#166534' },
  REJECTED:       { bg: '#fee2e2', color: '#991b1b' },
};

export default function AdminMarketplacePage() {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState('');
  const [detail, setDetail]     = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/marketplace/admin/pending', { headers: authHeaders() });
      setListings(res.data || []);
    } catch {
      setMsg(t('AdminMarketplacePage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const res = await axiosInstance.post(`/marketplace/admin/listings/${id}/approve`, {}, { headers: authHeaders() });
      setListings(p => p.filter(l => l.id !== id));
      if (detail?.id === id) setDetail(res.data);
      setMsg(t('AdminMarketplacePage.listingApproved'));
    } catch { setMsg(t('AdminMarketplacePage.approvalFailed')); }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) { setMsg(t('AdminMarketplacePage.rejectionReasonRequired')); return; }
    try {
      const res = await axiosInstance.post(`/marketplace/admin/listings/${id}/reject`, { reason: rejectReason }, { headers: authHeaders() });
      setListings(p => p.filter(l => l.id !== id));
      if (detail?.id === id) setDetail(res.data);
      setRejectReason('');
      setMsg(t('AdminMarketplacePage.listingRejected'));
    } catch { setMsg(t('AdminMarketplacePage.rejectionFailed')); }
  };

  const toggleFeatured = async (id, featured) => {
    try {
      const res = await axiosInstance.patch(`/marketplace/admin/listings/${id}/featured`, { featured }, { headers: authHeaders() });
      setListings(p => p.map(l => l.id === id ? res.data : l));
      if (detail?.id === id) setDetail(res.data);
    } catch { setMsg(t('AdminMarketplacePage.featuredToggleFailed')); }
  };

  const s = {
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 12 },
    btn:  (c = '#6366f1', sm) => ({
            padding: sm ? '4px 10px' : '7px 14px', background: c, color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontSize: sm ? 11 : 13, fontWeight: 600 }),
    msg:  { padding: '8px 14px', borderRadius: 6, fontSize: 13, marginBottom: 12, background: '#dbeafe', color: '#1e40af' },
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1000 }}>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('AdminMarketplacePage.pageTitle')}</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>{t('AdminMarketplacePage.pageSubtitle')}</div>

      {msg && <div style={s.msg}>{msg}</div>}

      {listings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <i className="fas fa-check-double" style={{ fontSize: 36, display: 'block', marginBottom: 12, color: '#d1d5db' }} />
          {t('AdminMarketplacePage.noPendingListings')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: detail ? '1fr 1fr' : '1fr', gap: 20 }}>
          {/* Listing table */}
          <div>
            {listings.map(l => {
              const sb = STATUS_BADGE[l.status] || STATUS_BADGE.PENDING_REVIEW;
              return (
                <div key={l.id} style={{ ...s.card, cursor: 'pointer', border: `2px solid ${detail?.id === l.id ? '#6366f1' : '#e5e7eb'}` }}
                     onClick={() => setDetail(l)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{l.firmName}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{l.partnerType}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, ...sb }}>{l.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 10 }}>{l.specialties}</div>
                  <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => approve(l.id)} style={s.btn('#16a34a', true)}>{t('AdminMarketplacePage.approve')}</button>
                    <button onClick={() => toggleFeatured(l.id, !l.featured)} style={s.btn('#d97706', true)}>
                      {l.featured ? t('AdminMarketplacePage.unfeature') : t('AdminMarketplacePage.feature')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detail panel */}
          {detail && (
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{detail.firmName}</span>
                <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  <i className="fas fa-times" />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                {[
                  [t('AdminMarketplacePage.partnerType'), detail.partnerType],
                  [t('AdminMarketplacePage.pricingTier'), detail.pricingTier],
                  [t('AdminMarketplacePage.regions'), detail.regionsServed],
                  [t('AdminMarketplacePage.frameworks'), detail.frameworksServed],
                  [t('AdminMarketplacePage.languages'), detail.languages],
                  [t('AdminMarketplacePage.rating'), t('AdminMarketplacePage.ratingValue', { rating: detail.averageRating, count: detail.reviewCount })],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>
              {detail.bio && (
                <div style={{ background: '#f9fafb', borderRadius: 6, padding: 10, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{t('AdminMarketplacePage.bio')}</div>
                  <p style={{ margin: 0, fontSize: 13 }}>{detail.bio}</p>
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('AdminMarketplacePage.rejectionReasonLabel')}</div>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => approve(detail.id)}      style={s.btn('#16a34a')}>{t('AdminMarketplacePage.approve')}</button>
                <button onClick={() => reject(detail.id)}       style={s.btn('#dc2626')}>{t('AdminMarketplacePage.reject')}</button>
                <button onClick={() => toggleFeatured(detail.id, !detail.featured)} style={s.btn('#d97706')}>
                  {detail.featured ? t('AdminMarketplacePage.removeFeature') : t('AdminMarketplacePage.markFeatured')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
